@file:OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class, ExperimentalFoundationApi::class)

package com.example.monetix.ui.dialogs

import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.animateContentSize
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AccountBalanceWallet
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDownward
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Search
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.*
import com.example.monetix.ui.components.MonetixTextField as OutlinedTextField
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.blur
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Brush
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.tween
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.compose.ui.window.PopupProperties
import androidx.compose.ui.zIndex
import coil3.compose.AsyncImage
import com.example.monetix.db.AccountEntity
import com.example.monetix.utils.rememberMediaHandler
import kotlinx.coroutines.delay

// ==========================================
// 🚀 LISTA BANCHE PRECOMPILATE "SMART" + SUPPORTO EMOJI
// ==========================================
data class BankPredef(
    val name: String,
    val domain: String? = null,
    val customUrl: String? = null,
    val customEmoji: String? = null
) {
    val url get() = customUrl ?: domain?.let { "https://icon.horse/icon/$it" }
}

val defaultBankSuggestions = listOf(
    BankPredef("Intesa Sanpaolo", "intesasanpaolo.com"),
    BankPredef("UniCredit", "unicredit.it"),
    // 🚀 FIX: Rimossi tutti i link Clearbit rotti. Ora usa il motore di default affidabile!
    BankPredef("Revolut", "revolut.com"),
    BankPredef("Poste Italiane", "poste.it"),
    BankPredef("Libretto Postale", "poste.it"),
    BankPredef("PayPal", "paypal.com"),
    BankPredef("HYPE", "hype.it"),
    BankPredef("Satispay", "satispay.com"),
    BankPredef("N26", "n26.com"),
    BankPredef("BBVA", "bbva.it"),
    BankPredef("Mediolanum", "bancamediolanum.it"),
    BankPredef("BPER Banca", "bper.it"),
    BankPredef("Fineco", "finecobank.com"),
    BankPredef("illimity", "illimity.com"),
    BankPredef("Trade Republic", "traderepublic.com"),
    BankPredef("ING Direct", "ing.it"),
    BankPredef("Credem", "credem.it"),
    BankPredef("Monte dei Paschi", "gruppomps.it"),
    BankPredef("Isybank", "isybank.com"),
    BankPredef("Findomestic", "findomestic.it"),
    BankPredef("Compass", "compass.it"),
    BankPredef("Banca Sella", "bancasella.it"),
    BankPredef("Widiba", "widiba.it"),
    BankPredef("Crédit Agricole", "credit-agricole.it")
)

fun getAutoBankLogoUrl(name: String): String? {
    val cleanName = name.trim().lowercase()
    if (cleanName.length < 2) return null

    val match = defaultBankSuggestions.firstOrNull { bank ->
        val bankName = bank.name.lowercase()
        // Cerca corrispondenze incrociate (es. "Sella" trova "Banca Sella")
        bankName.contains(cleanName) || cleanName.contains(bankName)
    }

    if (match != null) return match.url

    // 🚀 MAGIA: Se la banca non è in lista, inventiamo noi il dominio web!
    // Così qualsiasi banca o brand tu scriva, l'app proverà a trovarne il logo in automatico.
    if (cleanName.length >= 3) {
        val guessDomain = cleanName.replace(" ", "") + ".it"
        return "https://icon.horse/icon/$guessDomain"
    }

    return null
}

// ==========================================
// 🚀 IL COMPONENTE BLINDATO
// ==========================================
@Composable
private fun StableWebTextField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String = "",
    placeholder: String = "",
    modifier: Modifier = Modifier,
    trailingIcon: @Composable (() -> Unit)? = null,
    enabled: Boolean = true,
    keyboardType: KeyboardType = KeyboardType.Text,
    interactionSource: MutableInteractionSource = remember { MutableInteractionSource() }
) {
    var textFieldValue by remember {
        mutableStateOf(TextFieldValue(text = value, selection = TextRange(value.length)))
    }

    LaunchedEffect(value) {
        if (value != textFieldValue.text) {
            textFieldValue = textFieldValue.copy(
                text = value,
                selection = TextRange(value.length)
            )
        }
    }

    OutlinedTextField(
        value = textFieldValue,
        onValueChange = { newValue: TextFieldValue ->
            textFieldValue = newValue
            if (value != newValue.text) {
                onValueChange(newValue.text)
            }
        },
        modifier = modifier.fillMaxWidth().onFocusChanged { focusState ->
            if (focusState.isFocused && textFieldValue.text != "") {
                textFieldValue = TextFieldValue(text = "", selection = TextRange(0))
                onValueChange("")
            }
        },
        label = if (label.isNotBlank()) { { Text(label, style = MaterialTheme.typography.labelSmall) } } else null,
        placeholder = if (placeholder.isNotBlank()) { { Text(placeholder) } } else null,
        maxLines = 1,
        enabled = enabled,
        shape = RoundedCornerShape(12.dp),
        trailingIcon = trailingIcon,
        interactionSource = interactionSource,
        keyboardOptions = KeyboardOptions(keyboardType = keyboardType, autoCorrectEnabled = false)
    )
}

// ==========================================
// 🚀 POPUP RICERCA IMMAGINI WEB AUTOMATICA
// ==========================================
@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun WebImagePickerPopup(
    initialQuery: String,
    onDismiss: () -> Unit,
    onImageSelected: (String) -> Unit
) {
    var query by remember { mutableStateOf(initialQuery) }
    var results by remember { mutableStateOf<List<String>>(emptyList()) }
    val uriHandler = LocalUriHandler.current

    val popularBrands = remember {
        listOf("Intesa Sanpaolo", "Unicredit", "Postepay", "Revolut", "Hype", "BBVA", "N26", "Fineco", "ING", "BPER", "Banca Sella", "Mediolanum")
    }

    fun generateLinks(qRaw: String) {
        val q = qRaw.trim().lowercase().replace(" ", "")
        if (q.isEmpty()) {
            results = emptyList()
            return
        }
        val domain = if (q.contains(".")) q else "$q.com"
        val domainIt = if (q.contains(".")) q else "$q.it"

        results = listOf(
            "https://unavatar.io/$domain",
            "https://unavatar.io/$domainIt",
            "https://icon.horse/icon/$domain",
            "https://icon.horse/icon/$domainIt",
            "https://www.google.com/s2/favicons?domain=$domain&sz=128",
            "https://www.google.com/s2/favicons?domain=$domainIt&sz=128"
        ).distinct()
    }

    LaunchedEffect(initialQuery) {
        if (initialQuery.isNotBlank()) generateLinks(initialQuery)
    }

    AlertDialog(
        onDismissRequest = onDismiss,
        shape = RoundedCornerShape(28.dp),
        containerColor = Color(0xFF1E1B4B),
        title = {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Surface(
                    shape = CircleShape,
                    color = Color(0xFF60A5FA).copy(alpha = 0.2f),
                    modifier = Modifier.size(36.dp)
                ) {
                    Icon(
                        imageVector = Icons.Default.Search,
                        contentDescription = null,
                        tint = Color(0xFF60A5FA),
                        modifier = Modifier.padding(8.dp)
                    )
                }
                Spacer(Modifier.width(12.dp))
                Column {
                    Text(
                        "Ricerca Logo Banca 🌐",
                        style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                        color = Color.White
                    )
                    Text(
                        "Trova il logo ufficiale del tuo conto/banca",
                        style = MaterialTheme.typography.labelSmall,
                        color = Color.White.copy(alpha = 0.6f)
                    )
                }
            }
        },
        text = {
            Column(
                modifier = Modifier.fillMaxWidth().verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                OutlinedTextField(
                    value = query,
                    onValueChange = {
                        query = it
                        generateLinks(it)
                    },
                    label = { Text("Nome Banca o Istituto", color = Color(0xFFA0A0A0)) },
                    placeholder = { Text("es. Intesa, Revolut, Postepay...", color = Color(0xFFA0A0A0)) },
                    trailingIcon = {
                        if (query.isNotBlank()) {
                            IconButton(onClick = { query = ""; results = emptyList() }) {
                                Icon(Icons.Default.Close, contentDescription = "Cancella", tint = Color.White.copy(alpha = 0.6f))
                            }
                        } else {
                            Icon(Icons.Default.Search, contentDescription = null, tint = Color(0xFF60A5FA))
                        }
                    },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(14.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedTextColor = Color.White,
                        unfocusedTextColor = Color.White,
                        focusedContainerColor = Color.Black.copy(alpha = 0.35f),
                        unfocusedContainerColor = Color.Black.copy(alpha = 0.25f),
                        focusedBorderColor = Color(0xFF60A5FA),
                        unfocusedBorderColor = Color.White.copy(alpha = 0.20f)
                    )
                )

                Text("Banche e carte frequenti:", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha = 0.7f))
                LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    items(popularBrands) { brand ->
                        FilterChip(
                            selected = query.equals(brand, ignoreCase = true),
                            onClick = { query = brand; generateLinks(brand) },
                            label = { Text(brand, fontSize = 11.sp, fontWeight = FontWeight.SemiBold) },
                            colors = FilterChipDefaults.filterChipColors(
                                selectedContainerColor = Color(0xFF60A5FA),
                                selectedLabelColor = Color.White,
                                containerColor = Color.White.copy(alpha = 0.08f),
                                labelColor = Color.White.copy(alpha = 0.8f)
                            ),
                            border = null
                        )
                    }
                }

                if (results.isNotEmpty()) {
                    Text("Tocca il logo da usare:", style = MaterialTheme.typography.labelSmall, color = Color.White.copy(alpha = 0.7f))
                    LazyVerticalGrid(
                        columns = GridCells.Adaptive(64.dp),
                        horizontalArrangement = Arrangement.spacedBy(10.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp),
                        modifier = Modifier.height(160.dp).fillMaxWidth()
                    ) {
                        items(results) { url ->
                            Surface(
                                modifier = Modifier
                                    .aspectRatio(1f)
                                    .clickable { onImageSelected(url) },
                                shape = RoundedCornerShape(16.dp),
                                color = Color.White.copy(alpha = 0.15f),
                                border = BorderStroke(1.dp, Color.White.copy(alpha = 0.25f))
                            ) {
                                AsyncImage(
                                    model = url,
                                    contentDescription = null,
                                    modifier = Modifier.fillMaxSize().padding(10.dp),
                                    contentScale = ContentScale.Fit
                                )
                            }
                        }
                    }
                } else {
                    Box(modifier = Modifier.height(80.dp).fillMaxWidth(), contentAlignment = Alignment.Center) {
                        Text("Digita il nome di una banca per caricare i loghi", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))
                    }
                }

                HorizontalDivider(color = Color.White.copy(alpha = 0.10f))

                Button(
                    onClick = {
                        val q = if (query.isNotBlank()) "${query} logo icon" else "logo icon"
                        uriHandler.openUri("https://www.google.com/search?tbm=isch&q=${q.replace(" ", "+")}")
                    },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.12f), contentColor = Color.White)
                ) {
                    Icon(Icons.Outlined.Search, contentDescription = null, modifier = Modifier.size(18.dp), tint = Color(0xFF60A5FA))
                    Spacer(Modifier.width(8.dp))
                    Text("Cerca immagini su Google ↗", style = MaterialTheme.typography.labelMedium)
                }

                var manualUrl by remember { mutableStateOf("") }
                OutlinedTextField(
                    value = manualUrl,
                    onValueChange = { manualUrl = it },
                    label = { Text("O incolla URL immagine", color = Color(0xFFA0A0A0)) },
                    placeholder = { Text("https://...", color = Color(0xFFA0A0A0)) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(14.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedTextColor = Color.White,
                        unfocusedTextColor = Color.White,
                        focusedContainerColor = Color.Black.copy(alpha = 0.35f),
                        unfocusedContainerColor = Color.Black.copy(alpha = 0.25f),
                        focusedBorderColor = Color(0xFF60A5FA),
                        unfocusedBorderColor = Color.White.copy(alpha = 0.20f)
                    )
                )

                AnimatedVisibility(visible = manualUrl.isNotBlank()) {
                    Column(
                        modifier = Modifier.fillMaxWidth().padding(top = 4.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Surface(
                            modifier = Modifier.size(64.dp),
                            shape = RoundedCornerShape(16.dp),
                            color = Color.White.copy(alpha = 0.15f),
                            border = BorderStroke(1.dp, Color.White.copy(alpha = 0.25f))
                        ) {
                            AsyncImage(
                                model = manualUrl.trim(),
                                contentDescription = "Anteprima URL",
                                modifier = Modifier.fillMaxSize().padding(8.dp),
                                contentScale = ContentScale.Fit
                            )
                        }
                        Spacer(Modifier.height(10.dp))
                        Button(
                            onClick = { onImageSelected(manualUrl.trim()) },
                            shape = RoundedCornerShape(12.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF60A5FA), contentColor = Color.White)
                        ) {
                            Text("Usa questa immagine", fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
        },
        confirmButton = {},
        dismissButton = {
            TextButton(onClick = onDismiss) {
                Text("ANNULLA", color = Color.White.copy(alpha = 0.6f), fontWeight = FontWeight.Bold)
            }
        }
    )
}

// ==========================================

@OptIn(ExperimentalMaterial3Api::class, ExperimentalLayoutApi::class)
@Composable
fun AccountCreateOrEditDialog(
    initial: AccountEntity? = null,
    existingAccounts: List<AccountEntity> = emptyList(),
    onDismiss: () -> Unit,
    onSave: (String, Boolean, Boolean, String?, String?) -> Unit,
    title: String
) {
    val brandPrimary = MaterialTheme.colorScheme.primary

    var name by remember(initial) { mutableStateOf(initial?.name ?: "") }
    var visible by remember(initial) { mutableStateOf(!(initial?.isHidden ?: false)) }
    var isSavings by remember(initial) { mutableStateOf(initial?.isSavings ?: false) }
    var accLogoEmoji by remember(initial) { mutableStateOf(initial?.logoEmoji ?: "") }
    var accLogoUri by remember(initial) { mutableStateOf(initial?.logoUri) }

    var showEmojiInput by remember { mutableStateOf(false) }
    var showWebSearch by remember { mutableStateOf(false) }

    var suggestionsExpanded by remember { mutableStateOf(false) }
    val nameInteractionSource = remember { MutableInteractionSource() }
    val isNameFocused by nameInteractionSource.collectIsFocusedAsState()

    val dynamicSuggestions = remember(existingAccounts) {
        val userBanks = existingAccounts
            .filter { !it.isDeleted && it.name.isNotBlank() }
            .map {
                BankPredef(
                    name = it.name,
                    domain = null,
                    customUrl = it.logoUri,
                    customEmoji = it.logoEmoji
                )
            }

        (userBanks + defaultBankSuggestions).distinctBy { it.name.uppercase().trim() }
    }

    val mediaHandler = rememberMediaHandler(
        onImageResult = { uri ->
            if (uri != null) {
                accLogoUri = uri.toString()
                accLogoEmoji = ""
                showEmojiInput = false
            }
        },
        onVoiceResult = { }
    )

    val bgGradient = remember {
        Brush.verticalGradient(
            colors = listOf(
                Color(0xFF0284C7).copy(alpha = 0.75f),
                Color(0xFF0C4A6E),
                Color(0xFF0F172A)
            )
        )
    }

    val dialogBlur by animateDpAsState(
        targetValue = if (showWebSearch) 12.dp else 0.dp,
        animationSpec = tween(durationMillis = 300),
        label = "dialogBlur"
    )

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(
            usePlatformDefaultWidth = false,
            dismissOnClickOutside = true,
            dismissOnBackPress = true
        )
    ) {
        Surface(
            modifier = Modifier.fillMaxSize(),
            color = Color.Transparent
        ) {
            Box(modifier = Modifier.fillMaxSize().background(bgGradient).blur(dialogBlur)) {
                Box(modifier = Modifier.fillMaxSize().systemBarsPadding().imePadding()) {
                    Column(modifier = Modifier.fillMaxSize()) {
                        
                        // HEADER STILE REVOLUT
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 16.dp),
                            horizontalArrangement = Arrangement.SpaceBetween,
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            IconButton(
                                onClick = onDismiss,
                                modifier = Modifier.size(40.dp).background(Color.White.copy(alpha = 0.12f), CircleShape)
                            ) {
                                Icon(Icons.Default.ArrowBack, contentDescription = "Indietro", tint = Color.White)
                            }
                            IconButton(
                                onClick = { showWebSearch = true },
                                modifier = Modifier.size(40.dp).background(Color.White.copy(alpha = 0.12f), CircleShape)
                            ) {
                                Icon(Icons.Outlined.Search, contentDescription = "Cerca Web", tint = Color.White)
                            }
                        }

                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .weight(1f)
                                .verticalScroll(rememberScrollState())
                                .padding(horizontal = 24.dp, vertical = 8.dp)
                        ) {
                            Text(
                                text = if (initial != null) "Modifica conto" else "Aggiungi conto",
                                style = MaterialTheme.typography.headlineMedium.copy(fontWeight = FontWeight.Bold),
                                color = Color.White
                            )
                            Spacer(Modifier.height(24.dp))

                            // 🚀 ANTEPRIMA LIVE INTELLIGENTE (Stile Card "Dettagli")
                            val liveUri = accLogoUri.takeIf { !it.isNullOrBlank() } ?: getAutoBankLogoUrl(name)
                            val liveEmoji = accLogoEmoji.takeIf { it.isNotBlank() }

                            Surface(
                                shape = RoundedCornerShape(20.dp),
                                color = Color.White.copy(alpha = 0.08f),
                                border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)),
                                modifier = Modifier.fillMaxWidth()
                            ) {
                                Row(
                                    modifier = Modifier.padding(16.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Box(
                                        modifier = Modifier
                                            .size(48.dp)
                                            .clip(RoundedCornerShape(12.dp))
                                            .background(Color.White.copy(alpha = 0.10f)),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        when {
                                            !liveUri.isNullOrBlank() -> {
                                                val isWebImage = liveUri.startsWith("http", ignoreCase = true)
                                                AsyncImage(
                                                    model = liveUri,
                                                    contentDescription = null,
                                                    modifier = Modifier.fillMaxSize().padding(if (isWebImage) 8.dp else 0.dp).clip(RoundedCornerShape(12.dp)),
                                                    contentScale = if (isWebImage) ContentScale.Fit else ContentScale.Crop
                                                )
                                            }
                                            !liveEmoji.isNullOrBlank() -> Text(liveEmoji, fontSize = 24.sp)
                                            else -> Icon(Icons.Default.AccountBalanceWallet, null, tint = Color.White.copy(alpha=0.5f), modifier = Modifier.size(24.dp))
                                        }
                                    }
                                    Spacer(Modifier.width(16.dp))
                                    Column(modifier = Modifier.weight(1f)) {
                                        Text("Logo del Conto", style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
                                        Text("Tocca le opzioni sotto per cambiarlo", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
                                    }
                                }
                            }

                            Spacer(Modifier.height(16.dp))

                            // Tasti opzioni immagine (Emoji / Foto)
                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                Surface(onClick = { showEmojiInput = !showEmojiInput }, shape = RoundedCornerShape(16.dp), color = Color.White.copy(alpha = 0.10f), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.25f)), modifier = Modifier.weight(1f).height(40.dp)) {
                                    Box(contentAlignment = Alignment.Center) { Text("Emoji", color = Color.White, fontWeight = FontWeight.Bold) }
                                }
                                Surface(onClick = { mediaHandler.launchGallery() }, shape = RoundedCornerShape(16.dp), color = Color.White.copy(alpha = 0.10f), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.25f)), modifier = Modifier.weight(1f).height(40.dp)) {
                                    Box(contentAlignment = Alignment.Center) { Text("Galleria", color = Color.White, fontWeight = FontWeight.Bold) }
                                }
                            }

                            AnimatedVisibility(visible = showEmojiInput) {
                                Column(modifier = Modifier.padding(top = 16.dp)) {
                                    OutlinedTextField(
                                        value = accLogoEmoji,
                                        onValueChange = { if (it.length <= 3) { accLogoEmoji = it; accLogoUri = null } },
                                        label = { Text("Inserisci Emoji", color = Color(0xFFA0A0A0)) },
                                        singleLine = true,
                                        modifier = Modifier.fillMaxWidth(),
                                        shape = RoundedCornerShape(12.dp),
                                        colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF0284C7), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                                    )
                                }
                            }

                            Spacer(Modifier.height(24.dp))

                            // Toggle Tipo Conto
                            Surface(shape = RoundedCornerShape(24.dp), color = Color.White.copy(alpha = 0.10f), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)), modifier = Modifier.fillMaxWidth().height(48.dp)) {
                                Row(modifier = Modifier.fillMaxSize()) {
                                    Box(
                                        modifier = Modifier.weight(1f).fillMaxHeight().clip(RoundedCornerShape(24.dp))
                                            .clickable { isSavings = false }.background(if (!isSavings) Color(0xFF0284C7) else Color.Transparent),
                                        contentAlignment = Alignment.Center
                                    ) { Text("Conto Corrente", color = if (!isSavings) Color.White else Color(0xFFA0A0A0), fontWeight = FontWeight.Bold, fontSize = 13.sp) }
                                    Box(
                                        modifier = Modifier.weight(1f).fillMaxHeight().clip(RoundedCornerShape(24.dp))
                                            .clickable { isSavings = true }.background(if (isSavings) Color(0xFF0284C7) else Color.Transparent),
                                        contentAlignment = Alignment.Center
                                    ) { Text("Risparmio", color = if (isSavings) Color.White else Color(0xFFA0A0A0), fontWeight = FontWeight.Bold, fontSize = 13.sp) }
                                }
                            }

                            Spacer(Modifier.height(16.dp))

                            // Toggle Visibilità
                            Surface(shape = RoundedCornerShape(24.dp), color = Color.White.copy(alpha = 0.10f), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)), modifier = Modifier.fillMaxWidth().height(48.dp)) {
                                Row(modifier = Modifier.fillMaxSize()) {
                                    Box(
                                        modifier = Modifier.weight(1f).fillMaxHeight().clip(RoundedCornerShape(24.dp))
                                            .clickable { visible = true }.background(if (visible) Color(0xFF0284C7) else Color.Transparent),
                                        contentAlignment = Alignment.Center
                                    ) { Text("Visibile", color = if (visible) Color.White else Color(0xFFA0A0A0), fontWeight = FontWeight.Bold, fontSize = 13.sp) }
                                    Box(
                                        modifier = Modifier.weight(1f).fillMaxHeight().clip(RoundedCornerShape(24.dp))
                                            .clickable { visible = false }.background(if (!visible) Color(0xFF0284C7) else Color.Transparent),
                                        contentAlignment = Alignment.Center
                                    ) { Text("Nascosto", color = if (!visible) Color.White else Color(0xFFA0A0A0), fontWeight = FontWeight.Bold, fontSize = 13.sp) }
                                }
                            }

                            Spacer(Modifier.height(24.dp))

                            // Campo Nome
                            Box(modifier = Modifier.fillMaxWidth()) {
                                OutlinedTextField(
                                    value = name,
                                    onValueChange = { name = it; suggestionsExpanded = it.isNotBlank() },
                                    label = { Text("Nome della banca o del conto", color = Color(0xFFA0A0A0)) },
                                    singleLine = true,
                                    modifier = Modifier.fillMaxWidth(),
                                    shape = RoundedCornerShape(12.dp),
                                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF0284C7), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                                )

                                val filteredBanks = dynamicSuggestions.filter { it.name.contains(name, ignoreCase = true) }
                                DropdownMenu(
                                    expanded = suggestionsExpanded && filteredBanks.isNotEmpty(),
                                    onDismissRequest = { suggestionsExpanded = false },
                                    properties = PopupProperties(focusable = false),
                                    modifier = Modifier.fillMaxWidth(0.8f).background(Color(0xFF1C1C1E))
                                ) {
                                    filteredBanks.take(5).forEach { bank ->
                                        DropdownMenuItem(
                                            text = { Text(bank.name, fontWeight = FontWeight.SemiBold, color = Color.White) },
                                            leadingIcon = {
                                                when {
                                                    bank.url != null -> AsyncImage(model = bank.url, contentDescription = null, modifier = Modifier.size(24.dp))
                                                    bank.customEmoji != null -> Text(bank.customEmoji, fontSize = 20.sp)
                                                    else -> Icon(Icons.Default.AccountBalanceWallet, null, tint = Color.White, modifier = Modifier.size(24.dp))
                                                }
                                            },
                                            onClick = { name = bank.name; accLogoUri = bank.url; accLogoEmoji = bank.customEmoji ?: ""; suggestionsExpanded = false }
                                        )
                                    }
                                }
                            }

                            AnimatedVisibility(visible = name.isBlank()) {
                                Column(modifier = Modifier.padding(top = 16.dp)) {
                                    Text("Suggerimenti rapidi:", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
                                    Spacer(Modifier.height(8.dp))
                                    LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                        items(dynamicSuggestions.take(8)) { bank ->
                                            Surface(
                                                onClick = { name = bank.name; accLogoUri = bank.url; accLogoEmoji = bank.customEmoji ?: "" },
                                                shape = RoundedCornerShape(16.dp), color = Color.White.copy(alpha = 0.08f), border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))
                                            ) {
                                                Row(modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp), verticalAlignment = Alignment.CenterVertically) {
                                                    when {
                                                        bank.url != null -> AsyncImage(model = bank.url, contentDescription = null, modifier = Modifier.size(16.dp))
                                                        bank.customEmoji != null -> Text(bank.customEmoji, fontSize = 14.sp)
                                                    }
                                                    Spacer(Modifier.width(6.dp))
                                                    Text(bank.name, style = MaterialTheme.typography.bodySmall, color = Color.White)
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            Spacer(Modifier.height(100.dp)) // Spazio per il FAB
                        }
                    }

                    // FAB SALVA
                    FloatingActionButton(
                        onClick = {
                            val finalUri = accLogoUri.takeIf { !it.isNullOrBlank() } ?: getAutoBankLogoUrl(name)
                            val finalEmoji = accLogoEmoji.takeIf { it.isNotBlank() }
                            onSave(name.uppercase(), visible, isSavings, if (finalUri.isNullOrBlank()) finalEmoji else "", finalUri)
                        },
                        modifier = Modifier
                            .align(Alignment.BottomEnd)
                            .navigationBarsPadding()
                            .padding(end = 24.dp, bottom = 64.dp),
                        shape = CircleShape,
                        containerColor = if (name.isNotBlank()) Color(0xFF0284C7) else Color.White.copy(alpha = 0.12f),
                        contentColor = if (name.isNotBlank()) Color.White else Color(0xFFA0A0A0)
                    ) {
                        Icon(Icons.Default.ArrowDownward, contentDescription = "Salva", modifier = Modifier.size(24.dp))
                    }
                }
            }
        }
    }

    if (showWebSearch) {
        WebImagePickerPopup(
            initialQuery = name,
            onDismiss = { showWebSearch = false },
            onImageSelected = {
                accLogoUri = it
                accLogoEmoji = ""
                showWebSearch = false
            }
        )
    }
}